/**
 * Keyboard to held-state. The map is data so a later version can add a remap
 * UI without touching the reader. Values are 0 or 1.
 */

export interface KeyMap {
  moveLeft: string[];
  moveRight: string[];
  moveUp: string[];
  moveDown: string[];
  tiltBack: string[];
  tiltForward: string[];
  jump: string[];
  tuck: string[];
  reach: string[];
  pull: string[];
  restart: string[];
  pause: string[];
}

export const defaultKeyMap: KeyMap = {
  moveLeft: ["KeyA", "ArrowLeft"],
  moveRight: ["KeyD", "ArrowRight"],
  moveUp: ["KeyW", "ArrowUp"],
  moveDown: ["KeyS", "ArrowDown"],
  tiltBack: ["KeyQ"],
  tiltForward: ["KeyE"],
  jump: ["Space"],
  tuck: ["KeyL", "KeyC"],
  reach: ["KeyJ", "ShiftLeft", "ShiftRight"],
  pull: ["KeyK", "ControlLeft", "ControlRight"],
  restart: ["KeyR"],
  pause: ["Escape"],
};

export type KeyAction = keyof KeyMap;

/** Best-effort code from a KeyboardEvent without a `code` (synthetic events, some IMEs). */
export function keyToCode(key: string): string {
  if (key.length === 1) {
    const upper = key.toUpperCase();
    if (upper >= "A" && upper <= "Z") return `Key${upper}`;
    if (key === " ") return "Space";
    if (key >= "0" && key <= "9") return `Digit${key}`;
  }
  switch (key) {
    case "Shift":
      return "ShiftLeft";
    case "Control":
      return "ControlLeft";
    case "Spacebar":
      return "Space";
    default:
      return key;
  }
}

export class KeyboardReader {
  private readonly down = new Set<string>();
  private readonly codeToAction = new Map<string, KeyAction>();
  /** performance.now() of the most recent keydown edge, for the latency probe. */
  lastEdgeTime = 0;
  lastEdgeAction: KeyAction | null = null;
  private target: EventTarget | null = null;

  constructor(readonly map: KeyMap = defaultKeyMap) {
    for (const action of Object.keys(map) as KeyAction[]) {
      for (const code of map[action]) this.codeToAction.set(code, action);
    }
  }

  private readonly onDown = (e: Event): void => {
    const ke = e as KeyboardEvent;
    const code = ke.code || keyToCode(ke.key);
    const action = this.codeToAction.get(code);
    if (!action) return;
    if (ke.repeat) {
      ke.preventDefault();
      return;
    }
    if (!this.down.has(code)) {
      this.lastEdgeTime = performance.now();
      this.lastEdgeAction = action;
    }
    this.down.add(code);
    ke.preventDefault();
  };

  private readonly onUp = (e: Event): void => {
    const ke = e as KeyboardEvent;
    const code = ke.code || keyToCode(ke.key);
    // A field may stop the release from bubbling after a game key was pressed outside it.
    // Only consume releases we held: ordinary typing must keep its native keyup behavior.
    if (this.codeToAction.has(code) && this.down.delete(code)) {
      ke.preventDefault();
    }
  };

  private readonly onBlur = (): void => {
    this.cancelAll();
  };

  /** Discard held keys when the page is suspended without receiving their releases. */
  cancelAll(): void {
    this.down.clear();
  }

  attach(target: EventTarget): void {
    this.detach();
    this.target = target;
    target.addEventListener("keydown", this.onDown);
    target.addEventListener("keyup", this.onUp, { capture: true });
    target.addEventListener("blur", this.onBlur);
  }

  detach(): void {
    if (!this.target) return;
    this.target.removeEventListener("keydown", this.onDown);
    this.target.removeEventListener("keyup", this.onUp, { capture: true });
    this.target.removeEventListener("blur", this.onBlur);
    this.target = null;
  }

  /** Test hook: press or release a key code directly. */
  setKey(code: string, isDown: boolean): void {
    if (isDown) {
      if (!this.down.has(code)) {
        this.lastEdgeTime = performance.now();
        this.lastEdgeAction = this.codeToAction.get(code) ?? null;
      }
      this.down.add(code);
    } else this.down.delete(code);
  }

  held(action: KeyAction): boolean {
    for (const code of this.map[action]) if (this.down.has(code)) return true;
    return false;
  }

  /** -1, 0, or 1 from a pair of actions. */
  axis(negative: KeyAction, positive: KeyAction): number {
    return (this.held(positive) ? 1 : 0) - (this.held(negative) ? 1 : 0);
  }
}
